import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Toast as BaseToast } from "@base-ui/react/toast";
import { DismissIcon } from "../../primitives/Icons/Icons";
import css from "./Toast.module.css";

// Re-export the imperative hook for convenience
const { useToastManager } = BaseToast;
export { useToastManager };

const ToastProvider = ({ timeout = 5000, limit = 3, children }) => (
  <BaseToast.Provider timeout={timeout} limit={limit}>
    {children}
  </BaseToast.Provider>
);

ToastProvider.displayName = "ToastProvider";

ToastProvider.propTypes = {
  timeout: PropTypes.number,
  limit: PropTypes.number,
  children: PropTypes.node,
};

const ToastViewport = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BaseToast.Viewport
      ref={ref}
      className={clsx(css.viewport, className)}
      {...rest}
    >
      {children}
    </BaseToast.Viewport>
  ),
);

ToastViewport.displayName = "ToastViewport";

ToastViewport.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const ToastRoot = React.forwardRef(
  ({ toast, className, children, ...rest }, ref) => (
    <BaseToast.Root
      ref={ref}
      toast={toast}
      className={clsx(css.root, className)}
      data-type={toast?.type}
      {...rest}
    >
      {children}
    </BaseToast.Root>
  ),
);

ToastRoot.displayName = "ToastRoot";

ToastRoot.propTypes = {
  toast: PropTypes.object.isRequired,
  className: PropTypes.string,
  children: PropTypes.node,
};

const ToastContent = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={clsx(css.content, className)} {...rest}>
      {children}
    </div>
  ),
);

ToastContent.displayName = "ToastContent";

ToastContent.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const ToastTitle = React.forwardRef(({ className, children, ...rest }, ref) => (
  <BaseToast.Title ref={ref} className={clsx(css.title, className)} {...rest}>
    {children}
  </BaseToast.Title>
));

ToastTitle.displayName = "ToastTitle";

ToastTitle.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const ToastDescription = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BaseToast.Description
      ref={ref}
      className={clsx(css.description, className)}
      {...rest}
    >
      {children}
    </BaseToast.Description>
  ),
);

ToastDescription.displayName = "ToastDescription";

ToastDescription.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const ToastAction = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BaseToast.Action
      ref={ref}
      className={clsx(css.action, className)}
      {...rest}
    >
      {children}
    </BaseToast.Action>
  ),
);

ToastAction.displayName = "ToastAction";

ToastAction.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const ToastClose = React.forwardRef(({ className, children, ...rest }, ref) => (
  <BaseToast.Close
    ref={ref}
    aria-label="Close"
    className={clsx(css.close, className)}
    {...rest}
  >
    {children || <DismissIcon />}
  </BaseToast.Close>
));

ToastClose.displayName = "ToastClose";

ToastClose.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const Toast = {
  Provider: ToastProvider,
  Viewport: ToastViewport,
  Root: ToastRoot,
  Content: ToastContent,
  Title: ToastTitle,
  Description: ToastDescription,
  Action: ToastAction,
  Close: ToastClose,
};

export default Toast;
